"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ModelApi = exports.ArrApi = exports.BinApi = exports.StrApi = exports.ObjApi = exports.VecApi = exports.ValApi = exports.ConApi = exports.NodeApi = void 0;
const tslib_1 = require("tslib");
const printTree_1 = require("tree-dump/lib/printTree");
const get_1 = require("@jsonjoy.com/json-pointer/lib/get");
const util_1 = require("@jsonjoy.com/json-pointer/lib/util");
const find_1 = require("./find");
const nodes_1 = require("../../nodes");
const NodeEvents_1 = require("./NodeEvents");
const fanout_1 = require("thingies/lib/fanout");
const PatchBuilder_1 = require("../../../json-crdt-patch/PatchBuilder");
const fanout_2 = require("./fanout");
const ExtNode_1 = require("../../extensions/ExtNode");
const JsonCrdtDiff_1 = require("../../../json-crdt-diff/JsonCrdtDiff");
const diff = tslib_1.__importStar(require("../../../json-crdt-diff"));
const events_1 = require("./events");
// import {NodeValue} from './NodeValue';
const clock_1 = require("../../../json-crdt-patch/clock");
const proxy_1 = require("./proxy");
const breakPath = (path) => {
    if (!path)
        return [void 0, ''];
    if (typeof path === 'number')
        return [void 0, path];
    if (typeof path === 'string')
        path = (0, util_1.toPath)(path);
    switch (path.length) {
        case 0:
            return [void 0, ''];
        case 1:
            return [void 0, path[0]];
        default: {
            const key = path[path.length - 1];
            const parent = path.slice(0, -1);
            return [parent, key];
        }
    }
};
/**
 * A generic local changes API for a JSON CRDT node.
 *
 * @category Local API
 */
class NodeApi {
    constructor(node, api) {
        this.node = node;
        this.api = api;
        // ------------------------------------------------------------------- Events
        /**
         * @ignore
         * @deprecated Use `onChange()` and other `on*()` methods.
         */
        this.ev = undefined;
    }
    /**
     * Find a child node at the given path starting from this node.
     *
     * @param path Path to the child node to find.
     * @returns JSON CRDT node at the given path.
     */
    find(path) {
        let node = this.node;
        /**
         * @todo Remove this .child() loop, and remove the `.child()` method from JsonNode interface.
         */
        if (path === undefined) {
            if (typeof node.child === 'function') {
                const child = node.child();
                if (!child) {
                    if (node instanceof nodes_1.RootNode)
                        return node;
                    throw new Error('NO_CHILD');
                }
                return child;
            }
            throw new Error('CANNOT_IN');
        }
        if (typeof path === 'string' && !!path && path[0] !== '/')
            path = '/' + path;
        if (typeof path === 'number')
            path = [path];
        while (node instanceof nodes_1.ValNode)
            node = node.child();
        return (0, find_1.find)(node, path);
    }
    /**
     * Find a child node at the given path starting from this node and wrap it in
     * a local changes API.
     *
     * @param path Path to the child node to find.
     * @returns Local changes API for the child node at the given path.
     */
    in(path, noThrow = false) {
        try {
            const node = this.find(path);
            return this.api.wrap(node);
        }
        catch (error) {
            if (!noThrow)
                throw error;
        }
        return void 0;
    }
    asVal(noThrow) {
        if (this.node instanceof nodes_1.ValNode)
            return this.api.wrap(this.node);
        if (noThrow)
            return void 0;
        throw new Error('NOT_VAL');
    }
    asStr(noThrow) {
        if (this.node instanceof nodes_1.StrNode)
            return this.api.wrap(this.node);
        if (noThrow)
            return void 0;
        throw new Error('NOT_STR');
    }
    asBin(noThrow) {
        if (this.node instanceof nodes_1.BinNode)
            return this.api.wrap(this.node);
        if (noThrow)
            return void 0;
        throw new Error('NOT_BIN');
    }
    asArr(noThrow) {
        if (this.node instanceof nodes_1.ArrNode)
            return this.api.wrap(this.node);
        if (noThrow)
            return void 0;
        throw new Error('NOT_ARR');
    }
    asVec(noThrow) {
        if (this.node instanceof nodes_1.VecNode)
            return this.api.wrap(this.node);
        if (noThrow)
            return void 0;
        throw new Error('NOT_VEC');
    }
    asObj(noThrow) {
        if (this.node instanceof nodes_1.ObjNode)
            return this.api.wrap(this.node);
        if (noThrow)
            return void 0;
        throw new Error('NOT_OBJ');
    }
    asCon(noThrow) {
        if (this.node instanceof nodes_1.ConNode)
            return this.api.wrap(this.node);
        if (noThrow)
            return void 0;
        throw new Error('NOT_CON');
    }
    asExt(ext) {
        let extNode;
        const node = this.node;
        if (node instanceof ExtNode_1.ExtNode)
            extNode = node;
        if (node instanceof nodes_1.VecNode)
            extNode = node.ext();
        if (!extNode)
            throw new Error('NOT_EXT');
        const api = this.api.wrap(extNode);
        if (!ext)
            return api;
        if (api instanceof ext.Api)
            return api;
        throw new Error('NOT_EXT');
    }
    val(path, noThrow) {
        return this.in(path, noThrow)?.asVal(noThrow);
    }
    str(path, noThrow) {
        return this.in(path, noThrow)?.asStr(noThrow);
    }
    bin(path, noThrow) {
        return this.in(path, noThrow)?.asBin(noThrow);
    }
    arr(path, noThrow) {
        return this.in(path, noThrow)?.asArr(noThrow);
    }
    vec(path, noThrow) {
        return this.in(path, noThrow)?.asVec(noThrow);
    }
    obj(path, noThrow) {
        return this.in(path, noThrow)?.asObj(noThrow);
    }
    con(path, noThrow) {
        return this.in(path, noThrow)?.asCon(noThrow);
    }
    view() {
        return this.node.view();
    }
    select(path, leaf) {
        try {
            let node = path !== void 0 ? this.find(path) : this.node;
            if (leaf)
                while (node instanceof nodes_1.ValNode)
                    node = node.child();
            return this.api.wrap(node);
        }
        catch (_e) {
            return;
        }
    }
    read(path) {
        const view = this.view();
        if (Array.isArray(path))
            return (0, get_1.get)(view, path);
        if (!path)
            return view;
        let path2 = path + '';
        if (path && path2[0] !== '/')
            path2 = '/' + path2;
        return (0, get_1.get)(view, (0, util_1.toPath)(path2));
    }
    add(path, value) {
        const [parent, key] = breakPath(path);
        ADD: try {
            const node = this.select(parent, true);
            if (node instanceof ObjApi) {
                node.set({ [key]: value });
            }
            else if (node instanceof ArrApi || node instanceof StrApi || node instanceof BinApi) {
                const length = node.length();
                let index = 0;
                if (typeof key === 'number')
                    index = key;
                else if (key === '-')
                    index = length;
                else {
                    index = ~~key;
                    if (index + '' !== key)
                        break ADD;
                }
                if (index !== index)
                    break ADD;
                if (index < 0)
                    index = 0;
                if (index > length)
                    index = length;
                if (node instanceof ArrApi)
                    node.ins(index, Array.isArray(value) ? value : [value]);
                else if (node instanceof StrApi)
                    node.ins(index, value + '');
                else if (node instanceof BinApi) {
                    if (!(value instanceof Uint8Array))
                        break ADD;
                    node.ins(index, value);
                }
            }
            else if (node instanceof VecApi) {
                node.set([[~~key, value]]);
            }
            else
                break ADD;
            return true;
        }
        catch { }
        return false;
    }
    replace(path, value) {
        const [parent, key] = breakPath(path);
        REPLACE: try {
            const node = this.select(parent, true);
            if (node instanceof ObjApi) {
                const keyStr = key + '';
                if (!node.has(keyStr))
                    break REPLACE;
                node.set({ [key]: value });
            }
            else if (node instanceof ArrApi) {
                const length = node.length();
                let index = 0;
                if (typeof key === 'number')
                    index = key;
                else {
                    index = ~~key;
                    if (index + '' !== key)
                        break REPLACE;
                }
                if (index !== index || index < 0 || index > length)
                    break REPLACE;
                if (index === length)
                    node.ins(index, [value]);
                else
                    node.upd(index, value);
            }
            else if (node instanceof VecApi)
                node.set([[~~key, value]]);
            else
                break REPLACE;
            return true;
        }
        catch { }
        return false;
    }
    remove(path, length = 1) {
        const [parent, key] = breakPath(path);
        REMOVE: try {
            const node = this.select(parent, true);
            if (node instanceof ObjApi) {
                const keyStr = key + '';
                if (!node.has(keyStr))
                    break REMOVE;
                node.del([keyStr]);
            }
            else if (node instanceof ArrApi || node instanceof StrApi || node instanceof BinApi) {
                const len = node.length();
                let index = 0;
                if (typeof key === 'number')
                    index = key;
                else if (key === '-')
                    index = length;
                else {
                    index = ~~key;
                    if (index + '' !== key)
                        break REMOVE;
                }
                if (index !== index || index < 0 || index > len)
                    break REMOVE;
                node.del(index, Math.min(length, len - index));
            }
            else if (node instanceof VecApi) {
                node.set([[~~key, void 0]]);
            }
            else
                break REMOVE;
            return true;
        }
        catch { }
        return false;
    }
    // public value<V>(path: ApiPath): NodeValue<V> {
    //   return new NodeValue<V>(this, path);
    // }
    diff(value) {
        return diff.diff(this, value);
    }
    merge(...args) {
        const length = args.length;
        if (length === 1)
            return diff.merge(this, args[0]);
        else if (length === 2) {
            const [path, value] = args;
            return this.select(path, true)?.merge(value);
        }
        return;
    }
    op(operation) {
        if (!Array.isArray(operation))
            return false;
        const [type, path, value] = operation;
        switch (type) {
            case 'add':
                return this.add(path, value);
            case 'replace':
                return this.replace(path, value);
            case 'merge':
                return !!this.select(path)?.merge(value);
            case 'remove':
                return this.remove(path, value);
        }
    }
    get s() {
        return { $: this };
    }
    get $() {
        return (0, proxy_1.proxy$)((path) => {
            try {
                return this.api.wrap(this.find(path));
            }
            catch {
                return;
            }
        }, '$');
    }
    /**
     * Event target for listening to node changes. You can subscribe to `"view"`
     * events, which are triggered every time the node's view changes.
     *
     * ```ts
     * node.events.on('view', () => {
     *   // do something...
     * });
     * ```
     *
     * @ignore
     * @deprecated Use `onNodeChange()` and other `on*()` methods.
     */
    get events() {
        const et = this.ev;
        return et || (this.ev = new NodeEvents_1.NodeEvents(this));
    }
    /**
     * Attaches a listener which executes on every change that is executed
     * directly on this node. For example, if this is a "str" string node and
     * you insert or delete text, the listener will be executed. Or if
     * this is an "obj" object node and keys of this object are changed, this
     * listener will be executed.
     *
     * It does not trigger when child nodes are edit, to include those changes,
     * use `onSubtreeChange()` or `onChildChange()` methods.
     *
     * @see onChildChange()
     * @see onSubtreeChange()
     *
     * @param listener Callback called on every change that is executed directly
     *     on this node.
     * @returns Returns an unsubscribe function to stop listening to the events.
     */
    onSelfChange(listener) {
        return this.api.onChange.listen((event) => {
            if (event.direct().has(this.node))
                listener(event);
        });
    }
    /**
     * Attaches a listener which executes on every change that is applied to this
     * node's children. Hence, this listener will trigger only for *container*
     * nodes - nodes that can have child nodes, such as "obj", "arr", "vec", and
     * "val" nodes. It will not execute on changes made directly to this node.
     *
     * If you want to listen to changes on this node as well as its children, use
     * `onSubtreeChange()` method. If you want to listen to changes on this node
     * only, use `onSelfChange()` method.
     *
     * @see onSelfChange()
     * @see onSubtreeChange()
     *
     * @param listener Callback called on every change that is applied to
     *     children of this node.
     * @return Returns an unsubscribe function to stop listening to the events.
     */
    onChildChange(listener) {
        return this.api.onChange.listen((event) => {
            if (event.parents().has(this.node))
                listener(event);
        });
    }
    /**
     * Attaches a listener which executes on every change that is applied to this
     * node or any of its child nodes (recursively). This is equivalent to
     * combining both `onSelfChange()` and `onChildChange()` methods.
     *
     * @see onSelfChange()
     * @see onChildChange()
     *
     * @param listener Callback called on every change that is applied to this
     *     node or any of its child nodes.
     * @return Returns an unsubscribe function to stop listening to the events.
     */
    onSubtreeChange(listener) {
        return this.api.onChange.listen((event) => {
            const node = this.node;
            if (event.direct().has(node) || event.parents().has(node))
                listener(event);
        });
    }
    /**
     * Attaches a listener which executes on every change that is applied to this
     * node or any of its child nodes (recursively). The `kind` parameter allows
     * you to specify the type of changes to listen to: `"self"` for changes
     * directly on this node, `"child"` for changes on child nodes, and `"subtree"`
     * for both.
     *
     * @param kind The type of changes to listen to: `"self"`, `"child"`, or `"subtree"`.
     * @param listener Callback called on every change that matches the specified type.
     * @return Returns an unsubscribe function to stop listening to the events.
     */
    onNodeChange(kind, listener) {
        switch (kind) {
            case 'self':
                return this.onSelfChange(listener);
            case 'child':
                return this.onChildChange(listener);
            case 'subtree':
                return this.onSubtreeChange(listener);
        }
    }
    // -------------------------------------------------------------------- Debug
    toString(tab = '') {
        const name = this.constructor === NodeApi ? '*' : this.node.name();
        return 'api(' + name + ')' + (0, printTree_1.printTree)(tab, [(tab) => this.node.toString(tab)]);
    }
}
exports.NodeApi = NodeApi;
/**
 * Represents the local changes API for the `con` JSON CRDT node {@link ConNode}.
 *
 * @category Local API
 */
class ConApi extends NodeApi {
    /**
     * Returns a proxy object for this node.
     */
    get s() {
        return { $: this };
    }
}
exports.ConApi = ConApi;
/**
 * Local changes API for the `val` JSON CRDT node {@link ValNode}.
 *
 * @category Local API
 */
class ValApi extends NodeApi {
    /**
     * Get API instance of the inner node.
     * @returns Inner node API.
     */
    get() {
        return this.in();
    }
    /**
     * Sets the value of the node.
     *
     * @param json JSON/CBOR value or ID (logical timestamp) of the value to set.
     * @returns Reference to itself.
     */
    set(json) {
        const { api, node } = this;
        const builder = api.builder;
        const val = builder.constOrJson(json);
        api.builder.setVal(node.id, val);
        api.apply();
    }
    /**
     * Returns a proxy object for this node. Allows to access the value of the
     * node by accessing the `.val` property.
     */
    get s() {
        const self = this;
        const proxy = {
            $: this,
            get _() {
                const childNode = self.node.node();
                return self.api.wrap(childNode).s;
            },
        };
        return proxy;
    }
}
exports.ValApi = ValApi;
/**
 * Local changes API for the `vec` JSON CRDT node {@link VecNode}.
 *
 * @category Local API
 */
class VecApi extends NodeApi {
    /**
     * Get API instance of a child node.
     *
     * @param key Object key to get.
     * @returns A specified child node API.
     */
    get(key) {
        return this.in(key);
    }
    /**
     * Sets a list of elements to the given values.
     *
     * @param entries List of index-value pairs to set.
     * @returns Reference to itself.
     */
    set(entries) {
        const { api, node } = this;
        const { builder } = api;
        builder.insVec(node.id, entries.map(([index, json]) => [index, builder.constOrJson(json)]));
        api.apply();
    }
    push(...values) {
        const length = this.length();
        this.set(values.map((value, index) => [length + index, value]));
    }
    /**
     * Get the length of the vector without materializing it to a view.
     *
     * @returns Length of the vector.
     */
    length() {
        return this.node.elements.length;
    }
    /**
     * Returns a proxy object for this node. Allows to access vector elements by
     * index.
     */
    get s() {
        const proxy = new Proxy({}, {
            get: (target, prop, receiver) => {
                if (prop === '$')
                    return this;
                if (prop === 'toExt')
                    return () => this.asExt();
                const index = Number(prop);
                if (Number.isNaN(index))
                    throw new Error('INVALID_INDEX');
                const child = this.node.get(index);
                if (!child)
                    throw new Error('OUT_OF_BOUNDS');
                return this.api.wrap(child).s;
            },
        });
        return proxy;
    }
}
exports.VecApi = VecApi;
/**
 * Local changes API for the `obj` JSON CRDT node {@link ObjNode}.
 *
 * @category Local API
 */
class ObjApi extends NodeApi {
    /**
     * Get API instance of a child node.
     *
     * @param key Object key to get.
     * @returns A specified child node API.
     */
    get(key) {
        return this.in(key);
    }
    /**
     * Sets a list of keys to the given values.
     *
     * @param entries List of key-value pairs to set.
     * @returns Reference to itself.
     */
    set(entries) {
        const { api, node } = this;
        const { builder } = api;
        builder.insObj(node.id, Object.entries(entries).map(([key, json]) => [key, builder.constOrJson(json)]));
        api.apply();
    }
    /**
     * Deletes a list of keys from the object.
     *
     * @param keys List of keys to delete.
     * @returns Reference to itself.
     */
    del(keys) {
        const { api, node } = this;
        const { builder } = api;
        api.builder.insObj(node.id, keys.map((key) => [key, builder.con(undefined)]));
        api.apply();
    }
    /**
     * Checks if a key exists in the object.
     *
     * @param key Key to check.
     * @returns True if the key exists, false otherwise.
     */
    has(key) {
        return this.node.keys.has(key);
    }
    /** Diffs only keys present in `dst` object. */
    diffKeys(dst) {
        const diff = new JsonCrdtDiff_1.JsonCrdtDiff(this.api.model);
        const patch = diff.diffDstKeys(this.node, dst);
        return patch.ops.length ? patch : void 0;
    }
    /** Merges only keys present in `dst` object. */
    mergeKeys(dst) {
        const patch = this.diffKeys(dst);
        if (patch)
            this.api.model.applyLocalPatch(patch);
        return patch;
    }
    /**
     * Returns a proxy object for this node. Allows to access object properties
     * by key.
     */
    get s() {
        const proxy = new Proxy({}, {
            get: (target, prop, receiver) => {
                if (prop === '$')
                    return this;
                const key = String(prop);
                const child = this.node.get(key);
                if (!child)
                    throw new Error('NO_SUCH_KEY');
                return this.api.wrap(child).s;
            },
        });
        return proxy;
    }
}
exports.ObjApi = ObjApi;
/**
 * Local changes API for the `str` JSON CRDT node {@link StrNode}. This API
 * allows to insert and delete bytes in the UTF-16 string by referencing its
 * local character positions.
 *
 * @category Local API
 */
class StrApi extends NodeApi {
    /**
     * Inserts text at a given position.
     *
     * @param index Position at which to insert text.
     * @param text Text to insert.
     * @returns Reference to itself.
     */
    ins(index, text) {
        const { api, node } = this;
        api.onBeforeLocalChange.emit(api.next);
        const builder = api.builder;
        builder.pad();
        const nextTime = api.builder.nextTime();
        const id = new clock_1.Timestamp(builder.clock.sid, nextTime);
        const after = node.insAt(index, id, text);
        if (!after)
            throw new Error('OUT_OF_BOUNDS');
        builder.insStr(node.id, after, text);
        api.advance();
    }
    /**
     * Deletes a range of text at a given position.
     *
     * @param index Position at which to delete text.
     * @param length Number of UTF-16 code units to delete.
     * @returns Reference to itself.
     */
    del(index, length) {
        const { api, node } = this;
        api.onBeforeLocalChange.emit(api.next);
        const builder = api.builder;
        builder.pad();
        const spans = node.findInterval(index, length);
        if (!spans)
            throw new Error('OUT_OF_BOUNDS');
        node.delete(spans);
        builder.del(node.id, spans);
        api.advance();
    }
    /**
     * Given a character index in local coordinates, find the ID of the character
     * in the global coordinates.
     *
     * @param index Index of the character or `-1` for before the first character.
     * @returns ID of the character after which the given position is located.
     */
    findId(index) {
        const node = this.node;
        const length = node.length();
        const max = length - 1;
        if (index > max)
            index = max;
        if (index < 0)
            return node.id;
        const id = node.find(index);
        return id || node.id;
    }
    /**
     * Given a position in global coordinates, find the position in local
     * coordinates.
     *
     * @param id ID of the character.
     * @returns Index of the character in local coordinates. Returns -1 if the
     *          the position refers to the beginning of the string.
     */
    findPos(id) {
        const node = this.node;
        const nodeId = node.id;
        if (nodeId.sid === id.sid && nodeId.time === id.time)
            return -1;
        const chunk = node.findById(id);
        if (!chunk)
            return -1;
        const pos = node.pos(chunk);
        return pos + (chunk.del ? 0 : id.time - chunk.id.time);
    }
    /**
     * Get the length of the string without materializing it to a view.
     *
     * @returns Length of the string.
     */
    length() {
        return this.node.length();
    }
    /**
     * Returns a proxy object for this node.
     */
    get s() {
        return { $: this };
    }
}
exports.StrApi = StrApi;
/**
 * Local changes API for the `bin` JSON CRDT node {@link BinNode}. This API
 * allows to insert and delete bytes in the binary string by referencing their
 * local index.
 *
 * @category Local API
 */
class BinApi extends NodeApi {
    /**
     * Inserts octets at a given position.
     *
     * @param index Position at which to insert octets.
     * @param data Octets to insert.
     * @returns Reference to itself.
     */
    ins(index, data) {
        const { api, node } = this;
        const after = !index ? node.id : node.find(index - 1);
        if (!after)
            throw new Error('OUT_OF_BOUNDS');
        api.builder.insBin(node.id, after, data);
        api.apply();
    }
    /**
     * Deletes a range of octets at a given position.
     *
     * @param index Position at which to delete octets.
     * @param length Number of octets to delete.
     * @returns Reference to itself.
     */
    del(index, length) {
        const { api, node } = this;
        const spans = node.findInterval(index, length);
        if (!spans)
            throw new Error('OUT_OF_BOUNDS');
        api.builder.del(node.id, spans);
        api.apply();
    }
    /**
     * Get the length of the binary blob without materializing it to a view.
     *
     * @returns Length of the binary blob.
     */
    length() {
        return this.node.length();
    }
    /**
     * Returns a proxy object for this node.
     */
    get s() {
        return { $: this };
    }
}
exports.BinApi = BinApi;
/**
 * Local changes API for the `arr` JSON CRDT node {@link ArrNode}. This API
 * allows to insert and delete elements in the array by referencing their local
 * index.
 *
 * @category Local API
 */
class ArrApi extends NodeApi {
    /**
     * Get API instance of a child node.
     *
     * @param index Index of the element to get.
     * @returns Child node API for the element at the given index.
     */
    get(index) {
        return this.in(index);
    }
    /**
     * Inserts elements at a given position.
     *
     * @param index Position at which to insert elements.
     * @param values Values or schema of the elements to insert.
     */
    ins(index, values) {
        const { api, node } = this;
        const { builder } = api;
        const after = !index ? node.id : node.find(index - 1);
        if (!after)
            throw new Error('OUT_OF_BOUNDS');
        const valueIds = [];
        for (let i = 0; i < values.length; i++)
            valueIds.push(builder.json(values[i]));
        builder.insArr(node.id, after, valueIds);
        api.apply();
    }
    /**
     * Inserts elements at the end of the array.
     *
     * @param values Values or schema of the elements to insert at the end of the array.
     */
    push(...values) {
        const length = this.length();
        this.ins(length, values);
    }
    /**
     * Updates (overwrites) an element at a given position.
     *
     * @param index Position at which to update the element.
     * @param value Value or schema of the element to replace with.
     */
    upd(index, value) {
        const { api, node } = this;
        const ref = node.getId(index);
        if (!ref)
            throw new Error('OUT_OF_BOUNDS');
        const { builder } = api;
        builder.updArr(node.id, ref, builder.constOrJson(value));
        api.apply();
    }
    /**
     * Deletes a range of elements at a given position.
     *
     * @param index Position at which to delete elements.
     * @param length Number of elements to delete.
     * @returns Reference to itself.
     */
    del(index, length) {
        const { api, node } = this;
        const spans = node.findInterval(index, length);
        if (!spans)
            throw new Error('OUT_OF_BOUNDS');
        api.builder.del(node.id, spans);
        api.apply();
    }
    /**
     * Get the length of the array without materializing it to a view.
     *
     * @returns Length of the array.
     */
    length() {
        return this.node.length();
    }
    /**
     * Returns a proxy object that allows to access array elements by index.
     *
     * @returns Proxy object that allows to access array elements by index.
     */
    get s() {
        const proxy = new Proxy({}, {
            get: (target, prop, receiver) => {
                if (prop === '$')
                    return this;
                const index = Number(prop);
                if (Number.isNaN(index))
                    throw new Error('INVALID_INDEX');
                const child = this.node.getNode(index);
                if (!child)
                    throw new Error('OUT_OF_BOUNDS');
                return this.api.wrap(child).s;
            },
        });
        return proxy;
    }
}
exports.ArrApi = ArrApi;
/**
 * Local changes API for a JSON CRDT model. This class is the main entry point
 * for executing local user actions on a JSON CRDT document.
 *
 * @category Local API
 */
class ModelApi extends ValApi {
    /**
     * @param model Model instance on which the API operates.
     */
    constructor(model) {
        super(model.root, void 0);
        this.model = model;
        /**
         * Index of the next operation in builder's patch to be committed locally.
         *
         * @ignore
         */
        this.next = 0;
        /** Emitted before the model is reset, using the `.reset()` method. */
        this.onBeforeReset = new fanout_1.FanOut();
        /** Emitted after the model is reset, using the `.reset()` method. */
        this.onReset = new fanout_1.FanOut();
        /** Emitted before a patch is applied using `model.applyPatch()`. */
        this.onBeforePatch = new fanout_1.FanOut();
        /** Emitted after a patch is applied using `model.applyPatch()`. */
        this.onPatch = new fanout_1.FanOut();
        /** Emitted before local changes through `model.api` are applied. */
        this.onBeforeLocalChange = new fanout_1.FanOut();
        /** Emitted after local changes through `model.api` are applied. */
        this.onLocalChange = new fanout_1.FanOut();
        /** Emitted before any change is applied to the model. Combines
         * `onBeforeReset`, `onBeforePatch` and `onBeforeLocalChange`. */
        this.onBeforeChange = new fanout_2.MergeFanOut([this.onBeforeReset, this.onBeforePatch, this.onBeforeLocalChange], () => void 0);
        /** Emitted when the model changes. Combines `onReset`, `onPatch` and
         * `onLocalChange`. */
        this.onChange = new fanout_2.MergeFanOut([this.onReset, this.onPatch, this.onLocalChange], (raw) => new events_1.ChangeEvent(raw, this));
        /**
         * Emitted after local changes through `model.api` are applied. Same as
         * `.onLocalChange`, but this event buffered withing a microtask.
         *
         * @deprecated
         */
        this.onLocalChanges = new fanout_2.MicrotaskBufferFanOut(this.onLocalChange);
        /**
         * Emitted when the model changes. Same as `.onChange`, but this event is
         * emitted once per microtask.
         */
        this.onChanges = new fanout_2.MicrotaskBufferFanOut(this.onChange);
        /** Emitted before a transaction is started. */
        this.onBeforeTransaction = new fanout_1.FanOut();
        /** Emitted after transaction completes. */
        this.onTransaction = new fanout_1.FanOut();
        /** Emitted when the `model.api` builder change buffer is flushed. */
        this.onFlush = new fanout_1.FanOut();
        this.inTx = false;
        this.stopAutoFlush = undefined;
        // ---------------------------------------------------------------- SyncStore
        this.subscribe = (callback) => this.onChanges.listen(() => callback());
        this.getSnapshot = () => this.view();
        this.api = this;
        this.builder = new PatchBuilder_1.PatchBuilder(model.clock);
        model.onbeforereset = () => this.onBeforeReset.emit();
        model.onreset = (changed) => this.onReset.emit(changed);
        model.onbeforepatch = (patch) => this.onBeforePatch.emit(patch);
        model.onpatch = (patch) => this.onPatch.emit(patch);
    }
    wrap(node) {
        if (node instanceof nodes_1.ValNode)
            return node.api || (node.api = new ValApi(node, this));
        else if (node instanceof nodes_1.StrNode)
            return node.api || (node.api = new StrApi(node, this));
        else if (node instanceof nodes_1.BinNode)
            return node.api || (node.api = new BinApi(node, this));
        else if (node instanceof nodes_1.ArrNode)
            return node.api || (node.api = new ArrApi(node, this));
        else if (node instanceof nodes_1.ObjNode)
            return node.api || (node.api = new ObjApi(node, this));
        else if (node instanceof nodes_1.ConNode)
            return node.api || (node.api = new ConApi(node, this));
        else if (node instanceof nodes_1.VecNode)
            return node.api || (node.api = new VecApi(node, this));
        else if (node instanceof ExtNode_1.ExtNode) {
            if (node.api)
                return node.api;
            const extension = this.model.ext.get(node.extId);
            return (node.api = new extension.Api(node, this));
        }
        else
            throw new Error('UNKNOWN_NODE');
    }
    /**
     * Given a JSON/CBOR value, constructs CRDT nodes recursively out of it and
     * sets the root node of the model to the constructed nodes.
     *
     * @param json JSON/CBOR value to set as the view of the model.
     * @returns Reference to itself.
     *
     * @deprecated Use `.set()` instead.
     */
    root(json) {
        return this.set(json);
    }
    set(json) {
        super.set(json);
        return this;
    }
    /**
     * Apply locally any operations from the `.builder`, which haven't been
     * applied yet.
     */
    apply() {
        const ops = this.builder.patch.ops;
        const length = ops.length;
        const model = this.model;
        const from = this.next;
        this.onBeforeLocalChange.emit(from);
        for (let i = this.next; i < length; i++)
            model.applyOperation(ops[i]);
        this.next = length;
        model.tick++;
        this.onLocalChange.emit(from);
    }
    /**
     * Advance patch pointer to the end without applying the operations. With the
     * idea that they have already been applied locally.
     *
     * You need to manually call `this.onBeforeLocalChange.emit(this.next)` before
     * calling this method.
     *
     * @ignore
     */
    advance() {
        const from = this.next;
        this.next = this.builder.patch.ops.length;
        this.model.tick++;
        this.onLocalChange.emit(from);
    }
    transaction(callback) {
        if (this.inTx)
            callback();
        else {
            this.inTx = true;
            try {
                this.onBeforeTransaction.emit();
                callback();
                this.onTransaction.emit();
            }
            finally {
                this.inTx = false;
            }
        }
    }
    /**
     * Flushes the builder and returns a patch.
     *
     * @returns A JSON CRDT patch.
     * @todo Make this return undefined if there are no operations in the builder.
     */
    flush() {
        const patch = this.builder.flush();
        this.next = 0;
        if (patch.ops.length)
            this.onFlush.emit(patch);
        return patch;
    }
    /**
     * Begins to automatically flush buffered operations into patches, grouping
     * operations by microtasks or by transactions. To capture the patch, listen
     * to the `.onFlush` event.
     *
     * @returns Callback to stop auto flushing.
     */
    autoFlush(drainNow = false) {
        const drain = () => this.builder.patch.ops.length && this.flush();
        const onLocalChangesUnsubscribe = this.onLocalChanges.listen(drain);
        const onBeforeTransactionUnsubscribe = this.onBeforeTransaction.listen(drain);
        const onTransactionUnsubscribe = this.onTransaction.listen(drain);
        if (drainNow)
            drain();
        return (this.stopAutoFlush = () => {
            this.stopAutoFlush = undefined;
            onLocalChangesUnsubscribe();
            onBeforeTransactionUnsubscribe();
            onTransactionUnsubscribe();
        });
    }
}
exports.ModelApi = ModelApi;
